/**
 * FormulaTab — creative formula chips, cognitive attention overlay with inline
 * scanpath player, scene composition view with element inspector, and
 * collapsible lens analysis cards.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import type { CreativeWorkspaceData } from '../types';

interface Props {
  data: CreativeWorkspaceData;
  imageUrl?: string | null;
}

type OverlayMode = 'attention' | 'scene' | 'combined';

// ---------------------------------------------------------------------------
// Main tab
// ---------------------------------------------------------------------------

export function FormulaTab({ data, imageUrl }: Props) {
  const taxonomy = (data.taxonomy_extraction ?? {}) as Record<string, unknown>;
  const analyses = (data.domain_analyses ?? {}) as Record<string, Record<string, unknown>>;
  const sceneGraph = data.scene_graph as SceneGraphPayload | null;
  const visualAttention = data.visual_attention as VisualAttentionPayload | null;

  const hookAnalysis = (taxonomy.hook_analysis ?? null) as Record<string, unknown> | null;
  const angleAnalysis = (taxonomy.angle_analysis ?? null) as Record<string, unknown> | null;
  const ctaAnalysis = (taxonomy.cta_analysis ?? null) as Record<string, unknown> | null;
  const visualStyle = (taxonomy.visual_style ?? null) as Record<string, unknown> | null;
  const proofMechanisms = (taxonomy.proof_mechanisms ?? []) as Array<Record<string, unknown>>;
  const emotionalLevers = (taxonomy.emotional_levers ?? []) as Array<Record<string, unknown>>;

  const hasAttention = Boolean(visualAttention && (visualAttention.regions?.length ?? 0) > 0);
  const hasScene = Boolean(sceneGraph && (sceneGraph.elements?.length ?? 0) > 0);
  const hasAnyOverlay = hasAttention || hasScene;

  const [overlayMode, setOverlayMode] = useState<OverlayMode>(
    hasAttention ? 'attention' : hasScene ? 'scene' : 'combined'
  );

  return (
    <div className="p-5 space-y-5">
      {/* Formula header chips */}
      <FormulaHeader
        hookType={(hookAnalysis?.hook_type as string) ?? null}
        hookDisplay={(hookAnalysis?.hook_display_name as string) ?? null}
        angleArchetype={(angleAnalysis?.angle_archetype as string) ?? null}
        angleDisplay={(angleAnalysis?.angle_display_name as string) ?? null}
        ctaType={(ctaAnalysis?.cta_type as string) ?? null}
        ctaDisplay={(ctaAnalysis?.cta_display_name as string) ?? null}
        visualStyle={(visualStyle?.style_display_name as string) ?? null}
      />

      {/* Hook description */}
      {hookAnalysis && (
        <HookCard hook={hookAnalysis} />
      )}

      {/* Engagement signals */}
      {(proofMechanisms.length > 0 || emotionalLevers.length > 0) && (
        <EngagementSignals proofMechanisms={proofMechanisms} emotionalLevers={emotionalLevers} />
      )}

      {/* Visual analysis — attention + scene overlay */}
      {imageUrl && hasAnyOverlay && (
        <VisualAnalysisSection
          imageUrl={imageUrl}
          overlayMode={overlayMode}
          setOverlayMode={setOverlayMode}
          visualAttention={visualAttention}
          sceneGraph={sceneGraph}
          hasAttention={hasAttention}
          hasScene={hasScene}
        />
      )}

      {/* Fallback: just the image if we have scene data but no bbox overlays */}
      {imageUrl && !hasAnyOverlay && sceneGraph?.overall_composition && (
        <CompositionSummaryCard comp={sceneGraph.overall_composition} />
      )}

      {/* Lens analyses */}
      {Object.keys(analyses).length > 0 && (
        <LensAnalysesSection analyses={analyses} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Types (local, matching HAT + CompositionGraph shapes)
// ---------------------------------------------------------------------------

interface AttentionRegion {
  centroid_x?: number;
  centroid_y?: number;
  bbox_x?: number;
  bbox_y?: number;
  bbox_w?: number;
  bbox_h?: number;
  saliency?: number;
  fixation_probability?: number;
  predicted_dwell_ms?: number;
  foveal_coverage?: number;
  area_fraction?: number;
}

interface ScanpathStep {
  step?: number;
  fixation_x?: number;
  fixation_y?: number;
  saccade_from_x?: number;
  saccade_from_y?: number;
  fixation_duration_ms?: number;
  saccade_duration_ms?: number;
  timestamp_ms?: number;
  region_saliency?: number;
}

interface VisualAttentionPayload {
  image_width?: number;
  image_height?: number;
  heatmap_base64?: string;
  regions?: AttentionRegion[];
  scanpath?: ScanpathStep[];
  scan_pattern?: string;
  total_predicted_viewing_ms?: number;
}

interface SceneElement {
  id?: string;
  role?: string;
  description?: string;
  bounds?: { x_pct?: number; y_pct?: number; w_pct?: number; h_pct?: number; x?: number; y?: number; w?: number; h?: number };
  z_index?: number;
  concepts?: string[];
  visual_properties?: Record<string, unknown>;
}

interface OverallComposition {
  layout_type?: string;
  mood?: string;
  color_palette?: string[];
  focal_point?: { x?: number; y?: number };
  lighting_direction?: string;
  depth_layers?: number;
  primary_focal_point?: string;
}

interface SceneGraphPayload {
  elements?: SceneElement[];
  overall_composition?: OverallComposition;
}

// ---------------------------------------------------------------------------
// Formula header chips
// ---------------------------------------------------------------------------

function FormulaHeader({
  hookType, hookDisplay, angleArchetype, angleDisplay,
  ctaType, ctaDisplay, visualStyle,
}: {
  hookType?: string | null; hookDisplay?: string | null;
  angleArchetype?: string | null; angleDisplay?: string | null;
  ctaType?: string | null; ctaDisplay?: string | null;
  visualStyle?: string | null;
}) {
  const items = [
    { label: 'HOOK', value: hookDisplay || hookType },
    { label: 'ANGLE', value: angleDisplay || angleArchetype },
    { label: 'CTA', value: ctaDisplay || ctaType },
    { label: 'VISUAL', value: visualStyle },
  ].filter((i) => Boolean(i.value));

  if (items.length === 0) return null;

  return (
    <div>
      <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-2">
        Creative Formula
      </p>
      <div className="flex flex-wrap gap-2">
        {items.map(({ label, value }) => (
          <div
            key={label}
            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-center min-w-[90px]"
          >
            <p className="text-[9px] font-mono uppercase text-slate-400">{label}</p>
            <p className="text-xs font-semibold text-navy-900 mt-0.5">{value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hook card
// ---------------------------------------------------------------------------

function HookCard({ hook }: { hook: Record<string, unknown> }) {
  const display =
    (hook.hook_display_name as string) || (hook.hook_type as string) || '';
  const description =
    (hook.hook_description as string) || (hook.why_it_works as string) || '';
  const text = (hook.hook_text as string) || '';

  if (!display && !description) return null;

  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50 p-4 space-y-1.5">
      {display && <p className="text-sm font-semibold text-slate-800">{display}</p>}
      {description && <p className="text-xs text-slate-600 leading-relaxed">{description}</p>}
      {text && (
        <p className="text-[11px] font-mono text-slate-500 italic mt-1">&ldquo;{text}&rdquo;</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Engagement signals (proof mechanisms + emotional levers as chips)
// ---------------------------------------------------------------------------

function EngagementSignals({
  proofMechanisms,
  emotionalLevers,
}: {
  proofMechanisms: Array<Record<string, unknown>>;
  emotionalLevers: Array<Record<string, unknown>>;
}) {
  return (
    <div className="space-y-3">
      {proofMechanisms.length > 0 && (
        <div>
          <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-1.5">
            Proof Mechanisms
          </p>
          <div className="flex flex-wrap gap-2">
            {proofMechanisms.map((p, i) => {
              const name =
                (p.display_name as string) ||
                (p.proof_display_name as string) ||
                (p.mechanism_type as string) ||
                (p.proof_type as string) ||
                String(i);
              const desc = (p.description as string) || (p.evidence as string) || (p.proof_evidence as string) || '';
              return (
                <div
                  key={i}
                  title={desc}
                  className="text-[11px] px-2.5 py-1 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 font-medium cursor-default"
                >
                  {name}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {emotionalLevers.length > 0 && (
        <div>
          <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-1.5">
            Emotional Levers
          </p>
          <div className="flex flex-wrap gap-2">
            {emotionalLevers.map((e, i) => {
              const name =
                (e.display_name as string) ||
                (e.lever_type as string) ||
                (e.emotion as string) ||
                String(i);
              const desc = (e.evidence_text as string) || (e.evidence as string) || (e.trigger_text as string) || '';
              return (
                <div
                  key={i}
                  title={desc}
                  className="text-[11px] px-2.5 py-1 rounded-lg bg-violet-50 border border-violet-200 text-violet-700 font-medium cursor-default"
                >
                  {name}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Visual analysis section — unified overlay container
// ---------------------------------------------------------------------------

function VisualAnalysisSection({
  imageUrl,
  overlayMode,
  setOverlayMode,
  visualAttention,
  sceneGraph,
  hasAttention,
  hasScene,
}: {
  imageUrl: string;
  overlayMode: OverlayMode;
  setOverlayMode: (m: OverlayMode) => void;
  visualAttention: VisualAttentionPayload | null;
  sceneGraph: SceneGraphPayload | null;
  hasAttention: boolean;
  hasScene: boolean;
}) {
  const [gazeStep, setGazeStep] = useState(-1);
  const [playing, setPlaying] = useState(false);
  const [hoveredElement, setHoveredElement] = useState<string | null>(null);
  const animRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scanpath = visualAttention?.scanpath ?? [];
  const regions = visualAttention?.regions ?? [];
  const elements = sceneGraph?.elements ?? [];

  // Auto-advance scanpath steps
  useEffect(() => {
    if (!playing || scanpath.length === 0) return;
    const next = gazeStep + 1;
    if (next >= scanpath.length) {
      setPlaying(false);
      setGazeStep(-1);
      return;
    }
    const step = scanpath[next];
    const dwell = step.fixation_duration_ms ?? 400;
    animRef.current = setTimeout(() => {
      setGazeStep(next);
    }, next === 0 ? 0 : dwell);
    return () => { if (animRef.current) clearTimeout(animRef.current); };
  }, [playing, gazeStep, scanpath]);

  const startPlayback = useCallback(() => {
    setGazeStep(-1);
    setPlaying(true);
  }, []);

  const stopPlayback = useCallback(() => {
    setPlaying(false);
    setGazeStep(-1);
    if (animRef.current) clearTimeout(animRef.current);
  }, []);

  const showAttention = overlayMode === 'attention' || overlayMode === 'combined';
  const showScene = overlayMode === 'scene' || overlayMode === 'combined';

  const w = visualAttention?.image_width || 1080;
  const h = visualAttention?.image_height || 1080;

  // Active gaze point
  const activeStep = gazeStep >= 0 && gazeStep < scanpath.length ? scanpath[gazeStep] : null;

  return (
    <section className="space-y-2">
      {/* Mode toggle + scanpath controls */}
      <div className="flex items-center justify-between gap-2">
        <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400">
          Cognitive Analysis
        </p>
        <div className="flex items-center gap-1.5">
          {hasAttention && hasScene && (
            <div className="flex rounded-lg border border-slate-200 overflow-hidden text-[10px] font-mono">
              {(['attention', 'scene', 'combined'] as OverlayMode[]).map((m) => (
                <button
                  key={m}
                  onClick={() => setOverlayMode(m)}
                  className={`px-2 py-1 uppercase transition-colors ${
                    overlayMode === m
                      ? 'bg-slate-800 text-white'
                      : 'bg-white text-slate-500 hover:bg-slate-50'
                  }`}
                >
                  {m === 'combined' ? 'both' : m}
                </button>
              ))}
            </div>
          )}
          {hasAttention && scanpath.length > 0 && (showAttention) && (
            <button
              onClick={playing ? stopPlayback : startPlayback}
              className={`text-[10px] font-mono px-2.5 py-1 rounded-lg border transition-colors ${
                playing
                  ? 'bg-amber-50 border-amber-300 text-amber-700'
                  : 'bg-white border-slate-200 text-slate-600 hover:border-teal-300 hover:text-teal-600'
              }`}
            >
              {playing ? '⏹ stop' : '▶ gaze path'}
            </button>
          )}
        </div>
      </div>

      {/* Image + overlays */}
      <div className="rounded-lg border border-slate-200 overflow-hidden">
        <div className="relative bg-slate-900">
          <img src={imageUrl} alt="Creative" className="block w-full h-auto" />

          {/* Heatmap */}
          {showAttention && visualAttention?.heatmap_base64 && (
            <img
              src={`data:image/png;base64,${visualAttention.heatmap_base64}`}
              alt="Saliency heatmap"
              className="absolute inset-0 w-full h-full opacity-60"
              style={{ mixBlendMode: 'screen' }}
            />
          )}

          {/* SVG overlay */}
          <svg
            className="absolute inset-0 w-full h-full"
            viewBox={`0 0 ${w} ${h}`}
            preserveAspectRatio="none"
          >
            {/* Attention regions */}
            {showAttention && regions.map((r, idx) => {
              const isActive = activeStep &&
                Math.abs((activeStep.fixation_x ?? 0) - (r.centroid_x ?? 0)) < 80 &&
                Math.abs((activeStep.fixation_y ?? 0) - (r.centroid_y ?? 0)) < 80;
              const opacity = isActive ? 0.95 : Math.min(0.9, 0.15 + (r.saliency ?? 0) * 0.75);
              return (
                <rect
                  key={`r${idx}`}
                  x={r.bbox_x ?? 0}
                  y={r.bbox_y ?? 0}
                  width={r.bbox_w ?? 0}
                  height={r.bbox_h ?? 0}
                  fill={isActive ? 'rgba(20,184,166,0.12)' : 'none'}
                  stroke={isActive ? '#14b8a6' : `rgba(20,184,166,${opacity})`}
                  strokeWidth={isActive ? 4 : Math.max(2, (r.saliency ?? 0) * 5)}
                  vectorEffect="non-scaling-stroke"
                />
              );
            })}

            {/* Scanpath line */}
            {showAttention && !playing && scanpath.length > 1 && (
              <polyline
                points={scanpath.map((s) => `${s.fixation_x ?? 0},${s.fixation_y ?? 0}`).join(' ')}
                fill="none"
                stroke="rgba(251,191,36,0.5)"
                strokeWidth={2}
                strokeDasharray="5 3"
                vectorEffect="non-scaling-stroke"
              />
            )}

            {/* Scanpath fixation dots */}
            {showAttention && !playing && scanpath.map((s, idx) => (
              <circle
                key={`s${idx}`}
                cx={s.fixation_x ?? 0}
                cy={s.fixation_y ?? 0}
                r={Math.max(6, (s.fixation_duration_ms ?? 200) / 60)}
                fill="rgba(251,191,36,0.75)"
                stroke="white"
                strokeWidth={1.5}
                vectorEffect="non-scaling-stroke"
              />
            ))}

            {/* Animated gaze cursor */}
            {playing && activeStep && (
              <>
                {/* Foveal ring */}
                <circle
                  cx={activeStep.fixation_x ?? 0}
                  cy={activeStep.fixation_y ?? 0}
                  r={55}
                  fill="none"
                  stroke="rgba(251,191,36,0.35)"
                  strokeWidth={2}
                  vectorEffect="non-scaling-stroke"
                />
                {/* Gaze dot */}
                <circle
                  cx={activeStep.fixation_x ?? 0}
                  cy={activeStep.fixation_y ?? 0}
                  r={12}
                  fill="rgba(251,191,36,0.9)"
                  stroke="white"
                  strokeWidth={2}
                  vectorEffect="non-scaling-stroke"
                />
                <text
                  x={(activeStep.fixation_x ?? 0) + 16}
                  y={(activeStep.fixation_y ?? 0) - 8}
                  fontSize={28}
                  fill="rgba(251,191,36,0.95)"
                  fontFamily="monospace"
                  vectorEffect="non-scaling-stroke"
                >
                  {gazeStep + 1}
                </text>
              </>
            )}

            {/* Scene element boxes */}
            {showScene && elements.map((el, idx) => {
              const b = normalizeElementBounds(el.bounds);
              if (!b) return null;
              const id = el.id || el.role || String(idx);
              const isHovered = hoveredElement === id;
              // Convert 0-100 pct to pixel space
              const bx = (b.x / 100) * w;
              const by = (b.y / 100) * h;
              const bw = (b.w / 100) * w;
              const bh = (b.h / 100) * h;
              return (
                <g key={`el${idx}`}>
                  <rect
                    x={bx} y={by} width={bw} height={bh}
                    fill={isHovered ? 'rgba(139,92,246,0.12)' : 'rgba(139,92,246,0.04)'}
                    stroke={isHovered ? '#8b5cf6' : 'rgba(139,92,246,0.5)'}
                    strokeWidth={isHovered ? 3 : 1.5}
                    strokeDasharray={isHovered ? undefined : '6 3'}
                    vectorEffect="non-scaling-stroke"
                    style={{ cursor: 'pointer' }}
                  />
                  {isHovered && (
                    <text x={bx + 8} y={by + 22} fontSize={22} fill="#8b5cf6" fontFamily="monospace">
                      {el.role || el.id}
                    </text>
                  )}
                </g>
              );
            })}
          </svg>

          {/* Step counter badge */}
          {playing && gazeStep >= 0 && (
            <div className="absolute top-2 right-2 bg-amber-500 text-white text-[10px] font-mono px-2 py-0.5 rounded-full">
              fixation {gazeStep + 1}/{scanpath.length}
            </div>
          )}
        </div>

        {/* Scanpath step readout */}
        {showAttention && playing && activeStep && (
          <div className="bg-slate-900 px-4 py-2 border-t border-slate-700 flex items-center gap-3">
            <span className="text-[10px] font-mono text-amber-400 uppercase">
              Step {gazeStep + 1}
            </span>
            <span className="text-[11px] text-slate-300">
              Dwell {activeStep.fixation_duration_ms ?? 0}ms · Saliency {((activeStep.region_saliency ?? 0) * 100).toFixed(0)}%
            </span>
          </div>
        )}
      </div>

      {/* Attention regions list */}
      {showAttention && regions.length > 0 && !playing && (
        <div className="space-y-1">
          <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-1">
            Fixation Regions
          </p>
          <div className="grid grid-cols-1 gap-1.5">
            {[...regions]
              .sort((a, b) => (b.saliency ?? 0) - (a.saliency ?? 0))
              .map((r, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-3 rounded-lg border border-slate-100 bg-white px-3 py-2"
                >
                  <span className="text-[10px] font-mono text-slate-400 w-4">{idx + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="h-1.5 rounded-full bg-slate-100">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-teal-400 to-cyan-400"
                        style={{ width: `${(r.saliency ?? 0) * 100}%` }}
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-[10px] font-mono text-slate-500 shrink-0">
                    <span>{((r.saliency ?? 0) * 100).toFixed(0)}% sal</span>
                    <span>{r.predicted_dwell_ms ?? 0}ms</span>
                    <span>{((r.fixation_probability ?? 0) * 100).toFixed(0)}% fix</span>
                  </div>
                </div>
              ))}
          </div>
          {visualAttention?.scan_pattern && (
            <p className="text-[10px] font-mono text-slate-400 mt-1">
              Pattern: <span className="text-slate-600">{visualAttention.scan_pattern}</span>
              {visualAttention.total_predicted_viewing_ms && (
                <span className="ml-2">Total dwell: {(visualAttention.total_predicted_viewing_ms / 1000).toFixed(1)}s</span>
              )}
            </p>
          )}
        </div>
      )}

      {/* Scene element chips */}
      {showScene && elements.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400">
            Scene Elements
          </p>
          <div className="flex flex-wrap gap-1.5">
            {elements.map((el, idx) => {
              const id = el.id || el.role || String(idx);
              const isHovered = hoveredElement === id;
              return (
                <button
                  key={id}
                  onMouseEnter={() => setHoveredElement(id)}
                  onMouseLeave={() => setHoveredElement(null)}
                  className={`text-[11px] px-2.5 py-1 rounded-lg border transition-all text-left ${
                    isHovered
                      ? 'bg-violet-50 border-violet-300 text-violet-700'
                      : 'bg-white border-slate-200 text-slate-600 hover:border-violet-200'
                  }`}
                  title={el.description || ''}
                >
                  <span className="font-mono text-[9px] uppercase mr-1 opacity-60">{el.role}</span>
                  {el.id || el.role}
                </button>
              );
            })}
          </div>
          {/* Composition summary */}
          {sceneGraph?.overall_composition && (
            <CompositionSummaryCard comp={sceneGraph.overall_composition} compact />
          )}
        </div>
      )}
    </section>
  );
}

function normalizeElementBounds(
  b: SceneElement['bounds'] | undefined,
): { x: number; y: number; w: number; h: number } | null {
  if (!b) return null;
  if (b.x_pct != null) return { x: b.x_pct, y: b.y_pct ?? 0, w: b.w_pct ?? 0, h: b.h_pct ?? 0 };
  if (b.x != null) return { x: b.x * 100, y: (b.y ?? 0) * 100, w: (b.w ?? 0) * 100, h: (b.h ?? 0) * 100 };
  return null;
}

// ---------------------------------------------------------------------------
// Composition summary
// ---------------------------------------------------------------------------

function CompositionSummaryCard({
  comp,
  compact = false,
}: {
  comp: OverallComposition;
  compact?: boolean;
}) {
  const items = [
    comp.layout_type && { label: 'Layout', value: comp.layout_type.replace(/_/g, ' ') },
    comp.mood && { label: 'Mood', value: comp.mood },
    comp.lighting_direction && { label: 'Light', value: comp.lighting_direction },
    comp.depth_layers != null && { label: 'Depth', value: `${comp.depth_layers} layers` },
  ].filter(Boolean) as { label: string; value: string }[];

  if (items.length === 0 && !comp.color_palette) return null;

  return (
    <div className={compact ? 'flex flex-wrap items-center gap-2 mt-1' : 'rounded-xl border border-slate-100 bg-slate-50 p-3 flex flex-wrap gap-3'}>
      {items.map(({ label, value }) => (
        <div key={label} className={compact ? '' : ''}>
          <span className="text-[9px] font-mono uppercase text-slate-400">{label} </span>
          <span className="text-[11px] text-slate-600">{value}</span>
        </div>
      ))}
      {comp.color_palette && comp.color_palette.length > 0 && (
        <div className="flex items-center gap-1">
          <span className="text-[9px] font-mono uppercase text-slate-400 mr-0.5">Palette</span>
          {comp.color_palette.slice(0, 4).map((c, i) => (
            <span
              key={i}
              title={c}
              className="w-3 h-3 rounded-full border border-slate-200 inline-block"
              style={{ background: c.startsWith('#') ? c : c }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Lens analyses — collapsible cards with summary always visible
// ---------------------------------------------------------------------------

function LensAnalysesSection({
  analyses,
}: {
  analyses: Record<string, Record<string, unknown>>;
}) {
  const [openLens, setOpenLens] = useState<string | null>(null);

  return (
    <section className="space-y-2">
      <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400">
        Analysis Lenses
      </p>
      <div className="space-y-2">
        {Object.entries(analyses).map(([name, payload]) => (
          <LensCard
            key={name}
            lensName={name}
            payload={payload}
            isOpen={openLens === name}
            onToggle={() => setOpenLens(openLens === name ? null : name)}
          />
        ))}
      </div>
    </section>
  );
}

function LensCard({
  lensName,
  payload,
  isOpen,
  onToggle,
}: {
  lensName: string;
  payload: Record<string, unknown>;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const summary = (payload.summary as string) || '';
  const displayName = lensName.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  // Extract numeric scores for mini-bar display
  const scoreKeys = Object.entries(payload).filter(
    ([k, v]) => k.endsWith('_score') && typeof v === 'number'
  ) as [string, number][];

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="flex items-start justify-between gap-3 w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors"
      >
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-mono uppercase tracking-wider text-teal-600 mb-1">
            {displayName}
          </p>
          {summary && (
            <p className="text-xs text-slate-600 leading-relaxed line-clamp-2">{summary}</p>
          )}
          {scoreKeys.length > 0 && (
            <div className="flex items-center gap-3 mt-2">
              {scoreKeys.map(([k, v]) => (
                <div key={k} className="flex items-center gap-1.5">
                  <span className="text-[9px] font-mono text-slate-400">
                    {k.replace(/_score$/, '').replace(/_/g, ' ')}
                  </span>
                  <div className="w-14 h-1 rounded-full bg-slate-100">
                    <div
                      className="h-full rounded-full bg-teal-400"
                      style={{ width: `${(v / 10) * 100}%` }}
                    />
                  </div>
                  <span className="text-[9px] font-mono text-slate-500">{v}/10</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <span className="text-slate-400 text-xs mt-0.5 shrink-0">{isOpen ? '−' : '+'}</span>
      </button>
      {isOpen && (
        <div className="px-4 pb-3 border-t border-slate-100">
          <div className="space-y-2 pt-3">
            {Object.entries(payload)
              .filter(([k]) => k !== 'summary')
              .map(([k, v]) => {
                if (typeof v === 'object' || Array.isArray(v)) return null;
                return (
                  <div key={k} className="flex gap-2">
                    <span className="text-[10px] font-mono text-slate-400 uppercase w-28 shrink-0">
                      {k.replace(/_/g, ' ')}
                    </span>
                    <span className="text-xs text-slate-600">{String(v)}</span>
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
}
